import { AlertCircle, Plus, Trash2, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  type FundingLine,
  type LoanFunding,
  type LoanFundingSummary,
  useAllocateFunding,
  useDeallocateFunding,
  useFundingLines,
  useLoanFunding,
} from '@/hooks/use-funding';

function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function _formatBps(value: number): string {
  return `${(value * 10000).toFixed(0)} bps`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(3)}%`;
}

function getAvailableCapacity(line: FundingLine): number {
  return line.availableAmount ?? Math.max(0, line.facilityLimit - line.drawnAmount);
}

function getApiErrorMessage(error: unknown): string {
  const responseData = (
    error as {
      response?: {
        data?: {
          message?: string | string[];
          error?: { message?: string | string[] };
        };
      };
    }
  )?.response?.data;
  const message = responseData?.message ?? responseData?.error?.message;

  if (Array.isArray(message)) {
    return message[0] ?? 'Failed to allocate funding.';
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  if (error instanceof Error && error.message && !error.message.includes('status code 409')) {
    return error.message;
  }

  return 'Failed to allocate funding.';
}

interface NIMSummaryProps {
  loanId: string;
  fundingAllocations: LoanFunding[];
  summary?: LoanFundingSummary;
}

function NIMSummary({ loanId: _loanId, fundingAllocations, summary }: NIMSummaryProps) {
  const safeAllocations = Array.isArray(fundingAllocations) ? fundingAllocations : [];
  const totalAllocated =
    summary?.totalAllocated ??
    safeAllocations.reduce((sum, f) => sum + Number(f.allocatedAmount ?? 0), 0);
  const avgFundingCost = summary?.weightedCostRate ?? 0;
  const estimatedNim = summary?.nim ?? 0;

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Total Allocated</p>
        <p className="text-xl font-semibold text-gray-900">{formatCurrency(totalAllocated)}</p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Wtd Avg Funding Cost</p>
        <p className="text-xl font-semibold text-gray-900">{formatPercent(avgFundingCost)}</p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Est. NIM Spread</p>
        <p
          className={`text-xl font-semibold ${estimatedNim > 0 ? 'text-green-600' : 'text-gray-400'}`}
        >
          {totalAllocated > 0 ? formatPercent(estimatedNim) : '—'}
        </p>
        <p className="text-xs text-gray-500 mt-1">Loan rate − funding cost</p>
      </div>
    </div>
  );
}

interface AddFundingFormProps {
  loanId: string;
  fundingAllocations: LoanFunding[];
  onCancel: () => void;
  isSubmitting?: boolean;
}

function AddFundingForm({
  loanId,
  fundingAllocations,
  onCancel,
  isSubmitting,
}: AddFundingFormProps) {
  const [searchParams] = useSearchParams();
  const { data: availableFundingLines, isLoading } = useFundingLines();
  const allocateMutation = useAllocateFunding(loanId);
  const [formData, setFormData] = useState({
    fundingLineId: '',
    allocatedAmount: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const registerId = searchParams.get('register_id');
  const fundingLinesSearchParams = new URLSearchParams();
  if (registerId) {
    fundingLinesSearchParams.set('register_id', registerId);
  }
  const manageFundingLinesHref =
    fundingLinesSearchParams.size > 0
      ? `/treasury/funding-lines?${fundingLinesSearchParams.toString()}`
      : '/treasury/funding-lines';
  const allocatedFundingLineIds = new Set(
    fundingAllocations.map(allocation => allocation.fundingLineId),
  );
  const activeFundingLinesWithCapacity = (availableFundingLines ?? []).filter(
    line => line.status === 'active' && getAvailableCapacity(line) > 0,
  );
  const usableFundingLines = (availableFundingLines ?? []).filter(
    line =>
      line.status === 'active' &&
      getAvailableCapacity(line) > 0 &&
      !allocatedFundingLineIds.has(line.id),
  );
  const emptyStateMessage =
    activeFundingLinesWithCapacity.length > 0
      ? 'All available funding lines are already allocated to this loan. Manage funding lines or remove an existing allocation.'
      : 'No available funding lines to allocate. Create or manage funding lines before allocating funding to this loan.';
  const selectedFundingLine = usableFundingLines.find(line => line.id === formData.fundingLineId);
  const allocatedAmount = Number(formData.allocatedAmount);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.fundingLineId || Number.isNaN(allocatedAmount) || allocatedAmount <= 0) {
      setErrorMessage('Enter a valid allocated amount.');
      return;
    }

    if (!selectedFundingLine) {
      setErrorMessage('Select an available funding line.');
      return;
    }

    if (allocatedAmount > getAvailableCapacity(selectedFundingLine)) {
      setErrorMessage('Allocated amount exceeds available facility capacity.');
      return;
    }

    try {
      await allocateMutation.mutateAsync({
        fundingLineId: formData.fundingLineId,
        allocatedAmount,
      });
      setFormData({ fundingLineId: '', allocatedAmount: '' });
      onCancel();
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 bg-white rounded-lg border border-gray-200 space-y-4"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Funding Line</label>
          <select
            required
            value={formData.fundingLineId}
            onChange={e => {
              setErrorMessage(null);
              setFormData({ ...formData, fundingLineId: e.target.value });
            }}
            disabled={isLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select a funding line...</option>
            {usableFundingLines.map(line => (
              <option key={line.id} value={line.id}>
                {line.name} ({line.provider}) - {formatCurrency(getAvailableCapacity(line))}{' '}
                available
              </option>
            ))}
          </select>
          {!isLoading && usableFundingLines.length === 0 && (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-900">{emptyStateMessage}</p>
              <Link
                to={manageFundingLinesHref}
                className="mt-2 inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                Manage Funding Lines
              </Link>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Allocated Amount (AUD)
          </label>
          <input
            type="number"
            required
            step="0.01"
            min="0"
            value={formData.allocatedAmount}
            onChange={e => {
              setErrorMessage(null);
              setFormData({ ...formData, allocatedAmount: e.target.value });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="0.00"
          />
          {selectedFundingLine && (
            <p className="text-xs text-gray-500 mt-1">
              Available capacity: {formatCurrency(getAvailableCapacity(selectedFundingLine))}
            </p>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting || allocateMutation.isPending}
          className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          Cancel
        </button>
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting || allocateMutation.isPending || usableFundingLines.length === 0}
          className="flex items-center gap-2 px-3 py-2 text-white text-sm font-medium rounded-md hover: disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {(isSubmitting || allocateMutation.isPending) && (
            <Spinner size="lg" className="w-3.5 h-3.5 animate-spin" />
          )}
          Allocate Funding
        </Button>
      </div>
    </form>
  );
}

interface FundingTableProps {
  loanId: string;
  fundingAllocations: LoanFunding[];
  isLoading: boolean;
  onAddClick: () => void;
  showAddForm: boolean;
}

function FundingTable({
  loanId,
  fundingAllocations,
  isLoading,
  onAddClick,
  showAddForm,
}: FundingTableProps) {
  const deallocateMutation = useDeallocateFunding(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 text-gray-400 animate-spin mr-2" />
        <span className="text-sm text-gray-400">Loading funding…</span>
      </div>
    );
  }

  if (fundingAllocations.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500 mb-4">No funding allocations yet</p>
          <button
            onClick={onAddClick}
            disabled={showAddForm}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add your first funding allocation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-700">Funding Line</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Allocated Amount</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Cost Rate</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">Start Date</th>
            <th className="px-4 py-3 text-left font-medium text-gray-700">End Date</th>
            <th className="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {fundingAllocations.map(allocation => (
            <tr key={allocation.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 font-medium text-gray-900">{allocation.fundingLineName}</td>
              <td className="px-4 py-3 text-right text-gray-900">
                {formatCurrency(allocation.allocatedAmount)}
              </td>
              <td className="px-4 py-3 text-right text-gray-900">
                {formatPercent(allocation.costRate)}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {new Date(allocation.startDate).toLocaleDateString('en-AU')}
              </td>
              <td className="px-4 py-3 text-gray-600">
                {allocation.endDate
                  ? new Date(allocation.endDate).toLocaleDateString('en-AU')
                  : '−'}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => deallocateMutation.mutate(allocation.id)}
                  disabled={deallocateMutation.isPending}
                  className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-red-100 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                  title="Remove allocation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface FundingTabProps {
  loanId: string;
}

export function FundingTab({ loanId }: FundingTabProps) {
  const { data: fundingData, isLoading, isError } = useLoanFunding(loanId);
  const [showAddForm, setShowAddForm] = useState(false);

  const fundingAllocations = fundingData?.allocations ?? [];
  const fundingSummary = fundingData?.summary;

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span>Error loading funding information</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* NIM Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Funding & NIM</h3>
        </div>
        <NIMSummary
          loanId={loanId}
          fundingAllocations={fundingAllocations}
          summary={fundingSummary}
        />
      </div>

      {/* Add Funding Form */}
      {showAddForm && (
        <AddFundingForm
          loanId={loanId}
          fundingAllocations={fundingAllocations}
          onCancel={() => setShowAddForm(false)}
          isSubmitting={false}
        />
      )}

      {/* Funding Allocations Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Funding Allocations</h3>
          <Button
            variant="primary"
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm}
            className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Funding
          </Button>
        </div>
        <FundingTable
          loanId={loanId}
          fundingAllocations={fundingAllocations}
          isLoading={isLoading}
          onAddClick={() => setShowAddForm(true)}
          showAddForm={showAddForm}
        />
      </div>
    </div>
  );
}
